import { AnyAction } from 'redux';
import { ProductReference, SizePx } from '@/types';
import { convertAndScale } from '@/model/configuration/convertAndScale';
import {
  isDraftDesignConfiguration,
  isDraftSpecialTextConfiguration,
} from '@/model/configuration/isDraftConfigurationType';
import { validateMinDpi } from '@/model/configuration/validateConfiguration';
import { getCleanDraft } from '@/model/draft/getCleanDraft';
import { SaveProductError, getProductReference } from '@/model/product/getProductReference';
import { Thunk } from '@/redux/types';
import { setFilterDesignIdAction } from '@/components/draft/draftState/actions/setFilterDesignIdAction';
import { setSpecialFontSizeAction } from '@/components/draft/draftState/actions/setSpecialFontSizeAction';
import { withoutHistory } from '@/components/draft/draftState/history/withoutHistory';

export const saveProductAction =
  (progressCallback?: () => void): Thunk<Promise<ProductReference>, AnyAction> =>
  async (dispatch, getState) => {
    const { draft, draftDispatch } = getState();
    const cleanedDraft = getCleanDraft(draft, draftDispatch);

    return getProductReference(cleanedDraft, progressCallback, (configurations, tasks) => {
      let isSpecialFontConfigurationTooSmall = false;

      configurations.forEach((configuration) => {
        if (isDraftDesignConfiguration(configuration) && configuration.filter) {
          const productTask = tasks.find((t) => t.cid === configuration.cid);
          if (productTask && productTask.filterDesignId) {
            draftDispatch(
              withoutHistory(setFilterDesignIdAction(configuration, productTask.filterDesignId))
            );
          }
        }

        // update the size of the special text image from estimate to real value
        if (isDraftSpecialTextConfiguration(configuration)) {
          const task = tasks.find((t) => t.cid === configuration.cid);
          if (task && task.size) {
            const { width, height } = task.size;
            const { width: estimatedWidth, height: estimatedHeight } = configuration.task.size;
            if (estimatedWidth !== width || estimatedHeight !== height) {
              const realSize: SizePx = { width, height, unit: 'px' };
              const updatedScale = (configuration.scale * estimatedWidth) / width;
              // update the scale of the configuration so the width stays the same
              draftDispatch(
                withoutHistory(setSpecialFontSizeAction(configuration, updatedScale, realSize))
              );

              // revalidate the resolution of the special font configuration
              //  because the final image can be smaller than estimated
              isSpecialFontConfigurationTooSmall =
                isSpecialFontConfigurationTooSmall ||
                validateMinDpi(
                  configuration,
                  convertAndScale(realSize, updatedScale, configuration.printType)
                );
            }
          }
        }
      });

      if (isSpecialFontConfigurationTooSmall) {
        const error = new SaveProductError('It is not allowed to enlarge');
        error.handled = true; // add flag to skip error tracking
        throw error;
      }
    });
  };
